/**
 * The fleet planner — the availability calendar, week by week (Chris, 2026-09-11:
 * the planner's data lives inside the Studio, no parallel system).
 *
 * Rows are the engines ticked "Use in simulation"; columns are the season's
 * running weeks; a cell is the days that week the engine is OUT (its washout
 * week, an exam or repair, an expired boiler certificate, outside its active
 * dates — fleetAvailability.ts::engineOutOn, the one rule the build applies per
 * date). Beneath: the FEWEST steam and diesel engines available on any running
 * day of the week — what the build actually has that week — and, per month,
 * what the calendar did to the proposed timetable: steam turns run diesel for
 * want of a steam engine, turns with no engine at all (from the season table's
 * own per-date figures — the same numbers the board paper prints).
 */
import { useMemo } from 'react'
import type { FleetLoco } from '../types'
import { availabilityGrid, hasCalendar } from '../fleetAvailability'
import { engineLabel } from '../fleetRegister'
import type { Roster } from '../engineRoster'

export interface FleetPlannerMonth { mk: string; label: string; dates: string[] }
export interface FleetCover { swapped: number; noEngine: number; outDays: number; failureDays?: number }

export function FleetPlannerPanel({ locos, months, cover, roster }: {
  locos: FleetLoco[]
  /** The season's months with their RUNNING dates (thebigplan calendar / the applied set). */
  months: FleetPlannerMonth[]
  /** Per calendar month number: what the calendar did to the design (perDateMonth totals). */
  cover: Map<number, FleetCover>
  /** The season roster (engineRoster.ts): each engine's miles and days over the banked season. */
  roster?: Roster | null
}) {
  const dates = useMemo(() => months.flatMap((m) => m.dates), [months])
  const grid = useMemo(() => availabilityGrid(locos, dates), [locos, dates])
  const inUse = locos.filter((l) => l.inSimulation)
  const anyOut = grid.rows.some((r) => r.cells.some((c) => c.out > 0))
  const anyRule = hasCalendar(locos)
  const monthOf = (start: string) => Number(start.slice(5, 7))
  // A week's month label, printed once per month at the first week in it.
  const weekMonthLabels = grid.weeks.map((w, i) => (i === 0 || monthOf(w.start) !== monthOf(grid.weeks[i - 1].start) ? months.find((m) => Number(m.mk.split('-')[1]) === monthOf(w.start))?.label.split(' ')[0] ?? '' : ''))

  return (
    <section className="sh-card sh-card--pad">
      <div className="sh-section-label">Fleet availability — the season, week by week</div>
      <p className="stu-note" style={{ margin: '0 0 8px' }}>
        Each ticked engine&apos;s days OUT in each running week, from the register: its washout week (cooling down, washing out,
        firing up), its exams and repairs, its boiler certificate and its active dates. The build takes these day by day —
        an engine out that day is not in the fleet; a steam turn no steam engine can take runs diesel; a turn no engine can
        take is reported. The rows beneath are the fewest engines available on any running day of the week.
      </p>
      {inUse.length === 0 ? (
        <p className="stu-note">No engine is ticked &ldquo;Use in simulation&rdquo; — nothing to plan.</p>
      ) : dates.length === 0 ? (
        <p className="stu-note">The season&apos;s running calendar has not loaded yet — the grid appears with it.</p>
      ) : (
        <div className="stu-scroll">
          <table className="stu-table sh-fleet-grid">
            <thead>
              <tr>
                <th style={{ minWidth: 150 }}>Engine</th>
                {grid.weeks.map((w, i) => (
                  <th key={w.start} className="num" title={`Week commencing ${w.start} · ${w.dates.length} running day${w.dates.length === 1 ? '' : 's'}`}>
                    <span className="sh-dim" style={{ display: 'block', fontSize: 9 }}>{weekMonthLabels[i] || ' '}</span>{w.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((r) => (
                <tr key={r.engine.id}>
                  <td>
                    <strong>{engineLabel(r.engine)}</strong>
                    <span className="sh-dim" style={{ fontSize: 10 }}> · {r.engine.traction}{r.engine.fuel === 'oil' ? ' · oil' : ''}{r.engine.whitbyEnabled ? ' · Whitby' : ''}</span>
                  </td>
                  {r.cells.map((c, i) => (
                    <td key={i} className="num sh-mono" title={c.out > 0 ? `${engineLabel(r.engine)} out ${c.out} day${c.out === 1 ? '' : 's'} w/c ${grid.weeks[i].start}: ${c.reasons.join('; ')}` : `${engineLabel(r.engine)} available all week`}
                      style={c.out > 0 ? { background: c.out >= grid.weeks[i].dates.length ? '#f3c8c1' : '#fbe4d3', fontWeight: 700 } : undefined}>
                      {c.out > 0 ? c.out : ''}
                    </td>
                  ))}
                </tr>
              ))}
              {(['steam', 'diesel', 'railcar'] as const).filter((t) => inUse.some((l) => l.traction === t)).map((t) => (
                <tr key={t} className="sh-cost-total">
                  <td><strong>{t === 'steam' ? 'Steam' : t === 'diesel' ? 'Diesel' : 'Railcar'} available</strong> <span className="sh-dim" style={{ fontSize: 10 }}>fewest on any running day</span></td>
                  {grid.available[t].map((n, i) => {
                    const ticked = inUse.filter((l) => l.traction === t).length
                    return <td key={i} className="num sh-mono" style={n < ticked ? { color: n === 0 ? '#b03535' : '#b0791f', fontWeight: 700 } : undefined}>{n}</td>
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {inUse.length > 0 && dates.length > 0 && (
        <p className="stu-note" style={{ margin: '8px 0 0' }}>
          {anyOut
            ? <>What the calendar does to the proposed timetable, month by month (the season table&apos;s own per-date figures): {months.map((m) => {
              const c = cover.get(Number(m.mk.split('-')[1]))
              if (!c || m.dates.length === 0) return null
              return `${m.label.split(' ')[0]}: ${c.outDays} engine-day${c.outDays === 1 ? '' : 's'} out${c.swapped > 0 ? `, ${c.swapped} steam turn${c.swapped === 1 ? '' : 's'} run diesel` : ''}${(c.failureDays ?? 0) > 0 ? `, ${c.failureDays} FAILURE DAY${c.failureDays === 1 ? '' : 'S'}` : ''}`
            }).filter(Boolean).join(' · ') || 'the banked months carry no per-date figures yet — ★ Build Model'}. A FAILURE DAY has no engine of any traction for a turn: the timetable cannot run as designed, so it is valued as a non-running day and shown red — tick another engine, move an exam, or take a working out.</>
            : anyRule
              ? <>The engines&apos; dated rules (washout dates, certificates, active dates) all fall outside this season&apos;s running dates, so every date has the whole ticked fleet. They bite when the season they belong to is modelled.</>
              : <>No engine carries a dated rule yet — every date has the whole ticked fleet. Set washout dates, exams and certificates on the engines above and the build applies them day by day.</>}
        </p>
      )}
      {roster && Object.keys(roster.engines).length > 0 && (
        <>
          <div className="sh-section-label sh-mt">Each engine over the season — the roster</div>
          <p className="stu-note" style={{ margin: '0 0 6px' }}>
            Which engine works which turn is decided over the whole season: an engine with a contracted minimum is used first, its
            paid-for miles spread over the running days it is available and run in blocks of about a week, the minimum-contract
            engines interleaving; then the owned engines; then the cheapest contract steam for the day; then a diesel. Click an
            engine on a working turn on the Modelling stage to put a different engine on that turn from that month on.
            {roster.failureDays > 0 && <strong style={{ color: '#b03535' }}> {roster.failureDays} failure day{roster.failureDays === 1 ? '' : 's'} this season.</strong>}
            {roster.swappedTurns > 0 && <> {roster.swappedTurns} steam turn{roster.swappedTurns === 1 ? '' : 's'} run diesel for want of a steam engine.</>}
          </p>
          <div className="stu-scroll">
            <table className="stu-table">
              <thead>
                <tr><th>Engine</th><th>Traction</th><th className="num" title="Contracted minimum miles, paid whether run or not">Minimum</th><th className="num" title="Timetabled miles the roster gives the engine this season">Miles rostered</th><th className="num">Of minimum</th><th className="num">Days</th><th className="num" title="Separate runs of consecutive days — fewer means longer spells in steam">Blocks</th></tr>
              </thead>
              <tbody>
                {Object.values(roster.engines).sort((a, b) => (b.miles - a.miles)).map((e) => (
                  <tr key={e.engineId}>
                    <td><strong>{e.label}</strong></td>
                    <td className="sh-dim">{e.traction}</td>
                    <td className="num sh-mono">{e.minimum == null ? '—' : e.minimum.toLocaleString('en-GB')}</td>
                    <td className="num sh-mono">{Math.round(e.miles).toLocaleString('en-GB')}</td>
                    <td className="num sh-mono" style={e.minimum && e.miles < e.minimum ? { color: '#b0791f' } : undefined}>{e.minimum ? `${Math.round((e.miles / e.minimum) * 100)}%` : '—'}</td>
                    <td className="num sh-mono">{e.days}</td>
                    <td className="num sh-mono">{e.blocks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
